import React, { useState, useEffect, useMemo } from 'react';
import './TeamCompBuilds.css';
import DataService from '../../XXX/DataService';
import { RuneStats } from '../../NewWebSocket/utils/runeConfig';
import { runeIdMapping, parsePerkIds } from '../../NewWebSocket/utils/runeIdMapping';
import { ChampionItemBuildData } from '../../NewWebSocket/components/ChampionInfo/ChampionItemBuild';

// ── Re-export the Role type so the parent can use the same one ──
export type Role = 'top' | 'jgl' | 'mid' | 'bot' | 'sup';

/** Minimal champion reference the parent passes in */
export interface TeamSlot {
  role: Role;
  label: string;
  icon: string;              // role icon path
  championId: string | null;
  championName: string | null;
}

interface Props {
  blueTeam: TeamSlot[];
  redTeam: TeamSlot[];
}

// ═══════════════════════════════════════════════════════════════════
// Item-processing constants (mirrored from ItemBuildProgressionV2)
// ═══════════════════════════════════════════════════════════════════
const FINAL_SUPPORT_ITEM_IDS = new Set(['3869', '3870', '3871', '3876', '3877']);
const ALL_SUPPORT_ITEM_IDS = new Set([
  '3865', '3866', '3867',
  '3869', '3870', '3871', '3876', '3877',
]);
const JUNGLE_STARTER_IDS = new Set(['1101', '1102', '1103']);
const DEFAULT_JUNGLE_STARTER = '1101';
const DEFAULT_NON_JUNGLE_STARTER = '1055';
const BOOTS_IDS = new Set([
  '1001', '3006', '3009', '3020', '3047', '3111', '3117', '3158',
  '223009', '223020', '223047', '223111', '223158', '223006',
]);
const isBootsItem = (id: string): boolean => BOOTS_IDS.has(id);
const isJungleStarter = (id: string): boolean => JUNGLE_STARTER_IDS.has(id);

/** Build a display-ready item list from raw API data, respecting role constraints. */
function buildProcessedItems(
  build: ChampionItemBuildData,
  role: Role
): { starter: string | null; mainItems: string[] } {
  const isSupport = role === 'sup';
  const isJungle = role === 'jgl';
  const maxItems = role === 'bot' ? 7 : 6;

  // ── Starter ──
  let starter: string | null = null;
  if (isSupport) {
    starter = '3865';
  } else if (isJungle) {
    const dbStarter = build.starter?.item_id?.toString();
    starter = dbStarter && isJungleStarter(dbStarter) ? dbStarter : DEFAULT_JUNGLE_STARTER;
  } else {
    const dbStarter = build.starter?.item_id?.toString();
    starter = dbStarter && isJungleStarter(dbStarter) ? DEFAULT_NON_JUNGLE_STARTER : (dbStarter ?? null);
  }

  // ── Main items ──
  const mainItems: string[] = [];
  const addedIds = new Set<string>();
  let hasBoots = false;
  let hasFinalSupportItem = false;

  const coreIds = [
    build.coreItems.item1_id.toString(),
    build.coreItems.item2_id.toString(),
    build.coreItems.item3_id.toString(),
  ];

  for (const id of coreIds) {
    if (addedIds.has(id)) continue;
    if (isSupport && ALL_SUPPORT_ITEM_IDS.has(id)) {
      if (FINAL_SUPPORT_ITEM_IDS.has(id)) {
        hasFinalSupportItem = true;
        mainItems.push(id);
        addedIds.add(id);
      }
      continue;
    }
    mainItems.push(id);
    addedIds.add(id);
    if (isBootsItem(id)) hasBoots = true;
  }

  if (build.boots?.item_id && !hasBoots) {
    const bootsId = build.boots.item_id.toString();
    if (!addedIds.has(bootsId)) {
      mainItems.push(bootsId);
      addedIds.add(bootsId);
      hasBoots = true;
    }
  }

  if (build.supplementaryItems) {
    for (const item of build.supplementaryItems) {
      if (mainItems.length >= maxItems) break;
      const itemId = item.item_id.toString();
      if (addedIds.has(itemId)) continue;
      if (isBootsItem(itemId) && hasBoots) continue;
      if (isSupport && ALL_SUPPORT_ITEM_IDS.has(itemId)) {
        if (FINAL_SUPPORT_ITEM_IDS.has(itemId) && !hasFinalSupportItem) {
          hasFinalSupportItem = true;
          mainItems.push(itemId);
          addedIds.add(itemId);
        }
        continue;
      }
      mainItems.push(itemId);
      addedIds.add(itemId);
      if (isBootsItem(itemId)) hasBoots = true;
    }
  }

  if (isSupport) {
    const finalIdx = mainItems.findIndex(id => FINAL_SUPPORT_ITEM_IDS.has(id));
    if (finalIdx > 0) {
      const [finalItem] = mainItems.splice(finalIdx, 1);
      mainItems.unshift(finalItem);
    } else if (finalIdx === -1 && build.finalSupportItem?.item_id) {
      mainItems.unshift(build.finalSupportItem.item_id.toString());
    }
  }

  return { starter, mainItems: mainItems.slice(0, maxItems) };
}

// ═══════════════════════════════════════════════════════════════════
// Per-champion cached fetch result
// ═══════════════════════════════════════════════════════════════════
interface ChampionBuildInfo {
  itemBuild: ChampionItemBuildData | null;
  runes: RuneStats | null;
  loading: boolean;
}

// ═══════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════
const TeamCompBuilds: React.FC<Props> = ({ blueTeam, redTeam }) => {
  const [apiVersion, setApiVersion] = useState<string>('15.9.1');
  const [buildData, setBuildData] = useState<Record<string, ChampionBuildInfo>>({});

  // Fetch latest DDragon version once
  useEffect(() => {
    DataService.fetchLatestVersion()
      .then(v => setApiVersion(v))
      .catch(() => {});
  }, []);

  // All picked champion IDs across both teams
  const allChampionIds = useMemo(() => {
    const ids: string[] = [];
    [...blueTeam, ...redTeam].forEach(slot => {
      if (slot.championId) ids.push(slot.championId);
    });
    return ids;
  }, [blueTeam, redTeam]);

  // Fetch item build + runes for each champion we haven't fetched yet
  useEffect(() => {
    allChampionIds.forEach(champId => {
      if (buildData[champId]) return; // already fetched / loading

      setBuildData(prev => ({
        ...prev,
        [champId]: { itemBuild: null, runes: null, loading: true },
      }));

      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080';

      Promise.all([
        fetch(`${apiBase}/api/stats/champion/${champId}/item-build`)
          .then(r => (r.ok ? r.json() : null))
          .catch(() => null) as Promise<ChampionItemBuildData | null>,
        fetch(`${apiBase}/api/stats/champion/${champId}/runes`)
          .then(r => (r.ok ? r.json() : null))
          .then((data: RuneStats[] | null) => {
            if (data && data.length > 0) {
              const set = data[0];
              return {
                ...set,
                primary_perk_ids_array: parsePerkIds(set.primary_perk_ids),
                secondary_perk_ids_array: parsePerkIds(set.secondary_perk_ids),
              } as RuneStats;
            }
            return null;
          })
          .catch(() => null) as Promise<RuneStats | null>,
      ]).then(([itemBuild, runes]) => {
        setBuildData(prev => ({
          ...prev,
          [champId]: { itemBuild, runes, loading: false },
        }));
      });
    });
  }, [allChampionIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Image helpers ──
  const champImg = (id: string) =>
    `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/champion/${id}.png`;

  const itemImg = (id: number) =>
    `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/${id}.png`;

  const runeImg = (id: number): string => {
    const info = runeIdMapping[id];
    return info?.path
      ? `https://ddragon.leagueoflegends.com/cdn/img/perk-images/${info.path}`
      : '';
  };

  const treeImg = (styleId: number): string => {
    const map: Record<number, string> = {
      8000: '/Images/runes/7201_precision.png',
      8100: '/Images/runes/7200_domination.png',
      8200: '/Images/runes/7202_sorcery.png',
      8300: '/Images/runes/7203_inspiration.png',
      8400: '/Images/runes/7204_resolve.png',
    };
    return map[styleId] || '';
  };

  // ── Render one build card ──
  const renderCard = (slot: TeamSlot, team: 'blue' | 'red') => {
    if (!slot.championId) {
      return (
        <div key={`${team}-${slot.role}-build`} className="tcb-build-card empty">
          <div className="tcb-build-card-header">
            <img src={slot.icon} alt={slot.label} className="tcb-build-role-icon" />
            <span className="tcb-build-empty-label">No champion</span>
          </div>
        </div>
      );
    }

    const info = buildData[slot.championId];
    const isLoading = !info || info.loading;

    return (
      <div key={`${team}-${slot.role}-build`} className={`tcb-build-card ${team}`}>
        <div className="tcb-build-card-header">
          <img
            src={champImg(slot.championId)}
            alt={slot.championName || slot.championId}
            className="tcb-build-champ-img"
          />
          <span className="tcb-build-champ-name">
            {slot.championName || slot.championId}
          </span>
        </div>

        {isLoading ? (
          <div className="tcb-build-loading">Loading...</div>
        ) : (
          <div className="tcb-build-card-body">
            {/* Runes */}
            {info.runes && (
              <div className="tcb-build-runes">
                <img
                  src={treeImg(info.runes.primary_style_id)}
                  alt={info.runes.primary_style || 'Primary'}
                  className="tcb-rune-tree-icon"
                  title={info.runes.primary_style}
                />
                {info.runes.primary_perk_ids_array?.map((id, idx) => (
                  <img
                    key={`p-${id}-${idx}`}
                    src={runeImg(id)}
                    alt={runeIdMapping[id]?.name || String(id)}
                    className={`tcb-rune-icon ${idx === 0 ? 'keystone' : ''}`}
                    title={runeIdMapping[id]?.name}
                  />
                ))}
                <span className="tcb-rune-divider" />
                <img
                  src={treeImg(info.runes.secondary_style_id)}
                  alt={info.runes.secondary_style || 'Secondary'}
                  className="tcb-rune-tree-icon secondary"
                  title={info.runes.secondary_style}
                />
                {info.runes.secondary_perk_ids_array?.map((id, idx) => (
                  <img
                    key={`s-${id}-${idx}`}
                    src={runeImg(id)}
                    alt={runeIdMapping[id]?.name || String(id)}
                    className="tcb-rune-icon"
                    title={runeIdMapping[id]?.name}
                  />
                ))}
              </div>
            )}

            {/* Shards */}
            {info.runes && (
              <div className="tcb-build-shards">
                {[info.runes.shard_offense, info.runes.shard_flex, info.runes.shard_defense].map(
                  (name, idx) => (
                    <span key={idx} className="tcb-shard-label" title={name}>
                      {name || '?'}
                    </span>
                  )
                )}
              </div>
            )}

            {/* Items */}
            {info.itemBuild &&
              (() => {
                const processed = buildProcessedItems(info.itemBuild, slot.role);
                return (
                  <div className="tcb-build-items">
                    {processed.starter && (
                      <img
                        src={itemImg(parseInt(processed.starter))}
                        alt="Starter"
                        className="tcb-item-icon starter"
                        title="Starter"
                      />
                    )}
                    {processed.starter && <span className="tcb-item-separator">→</span>}
                    {processed.mainItems.map((itemId, idx) => (
                      <img
                        key={idx}
                        src={itemImg(parseInt(itemId))}
                        alt={`Item ${idx + 1}`}
                        className="tcb-item-icon"
                        title={`Item ${idx + 1}`}
                      />
                    ))}
                  </div>
                );
              })()}
          </div>
        )}
      </div>
    );
  };

  // Don't render section if nobody is picked
  const hasAny = allChampionIds.length > 0;
  if (!hasAny) return null;

  return (
    <div className="tcb-builds-section">
      <h2 className="tcb-builds-title">Builds &amp; Runes</h2>
      <div className="tcb-builds-grid">
        <div className="tcb-builds-team blue">
          <h3>Blue Side</h3>
          {blueTeam.map(slot => renderCard(slot, 'blue'))}
        </div>
        <div className="tcb-builds-team red">
          <h3>Red Side</h3>
          {redTeam.map(slot => renderCard(slot, 'red'))}
        </div>
      </div>
    </div>
  );
};

export default TeamCompBuilds;
